import type {
	IBackground,
	IBackgroundImage,
	IGroup,
	ILayer,
	IStaticAudio,
	IStaticCircle,
	IStaticImage,
	IStaticPath,
	IStaticRect,
	IStaticText,
	IStaticTriangle,
	IStaticVector,
	IStaticVideo,
} from "../../types";
import { LayerType } from "../common/constants";

class ObjectExporter {
	public export(item: ILayer, options: Required<ILayer>, inGroup = false): ILayer {
		let object;
		switch (item.type) {
			case LayerType.STATIC_IMAGE:
				object = this.staticImage(item, options, inGroup);
				break;
			case LayerType.BACKGROUND_IMAGE:
				object = this.backgroundImage(item, options, inGroup);
				break;
			case LayerType.STATIC_VIDEO:
				object = this.staticVideo(item, options, inGroup);
				break;
			case LayerType.STATIC_TEXT:
				object = this.staticText(item, options, inGroup);
				break;
			case LayerType.STATIC_VECTOR:
				object = this.staticVector(item, options, inGroup);
				break;
			case LayerType.STATIC_PATH:
				object = this.staticPath(item, options, inGroup);
				break;
			case LayerType.BACKGROUND:
				object = this.background(item, options, inGroup);
				break;
			case LayerType.GROUP.toLowerCase():
				object = this.group(item, options, inGroup);
				break;
			case LayerType.STATIC_AUDIO:
				object = this.staticAudio(item, options, inGroup);
				break;
			case LayerType.STATIC_RECT:
				object = this.staticRect(item, options, inGroup);
				break;
			case LayerType.STATIC_CIRCLE:
				object = this.staticCircle(item, options, inGroup);
				break;
			case LayerType.STATIC_TRIANGLE:
				object = this.staticTriangle(item, options, inGroup);
				break;
			default:
				object = this.background(item, options, inGroup);
		}
		return object;
	}

	public staticText(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticText {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const {
			fontFamily,
			textAlign,
			fontSize,
			fontWeight,
			charSpacing,
			textType,
			customFontName,
			colorNumber,
			shadowColorNumber,
			brandId,
			fontType,
			fixedSize,
			fixedWidthValue,
			maximumFontSize,
			fixedHeightValue,
			lineHeight,
			fill,
			text,
			angle,
			underline,
			linethrough,
			backgroundColor,
			textBackgroundColor,
			fontURL,
			metadata,
			fontStyle,
		} = item as IStaticText;
		const object = {
			...baseOptions,
			charSpacing,
			fontWeight,
			fill,
			fontFamily,
			fontSize,
			lineHeight,
			text,
			textAlign,
			angle,
			underline,
			linethrough,
			backgroundColor,
			textBackgroundColor,
			fontURL,
			metadata,
			textType,
			customFontName,
			fontStyle,
			colorNumber,
			shadowColorNumber,
			brandId,
			fontType,
			fixedSize,
			fixedWidthValue,
			maximumFontSize,
			fixedHeightValue,
		};
		return object;
	}

	public staticRect(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticRect {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { colorNumber, shadowColorNumber, brandId, fill, metadata, rx, ry, shapeType, strokeColorNumber } =
			item as IStaticRect;
		const object = {
			...baseOptions,
			fill,
			metadata,
			colorNumber,
			shadowColorNumber,
			brandId,
			rx,
			ry,
			shapeType,
			strokeColorNumber,
		};
		return object;
	}

	public staticCircle(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticCircle {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { colorNumber, shadowColorNumber, brandId, fill, metadata, radius, strokeColorNumber, shapeType } =
			item as IStaticCircle;
		const object = {
			...baseOptions,
			fill,
			metadata,
			colorNumber,
			shadowColorNumber,
			brandId,
			radius,
			shapeType,
			strokeColorNumber,
		};
		return object;
	}

	public staticTriangle(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticTriangle {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { colorNumber, shadowColorNumber, brandId, fill, metadata, shapeType, strokeColorNumber } =
			item as IStaticTriangle;
		const object = {
			...baseOptions,
			fill,
			metadata,
			colorNumber,
			shadowColorNumber,
			brandId,
			shapeType,
			strokeColorNumber,
		};
		return object;
	}

	public staticImage(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticImage {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const {
			src,
			cropX,
			cropY,
			metadata,
			imageType,
			logoConfigType,
			isLogo,
			logoType,
			horizontalAlignment,
			verticalAlignment,
			brandId,
		} = item as IStaticImage;
		const object = {
			...baseOptions,
			src,
			cropX,
			cropY,
			metadata,
			imageType,
			logoConfigType,
			isLogo,
			logoType,
			horizontalAlignment,
			verticalAlignment,
			brandId,
		};

		return object;
	}

	public backgroundImage(item: ILayer, options: Required<ILayer>, inGroup: boolean): IBackgroundImage {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const {
			src,
			cropX,
			cropY,
			metadata,
			imageType,
			logoConfigType,
			isLogo,
			logoType,
			horizontalAlignment,
			verticalAlignment,
			brandId,
		} = item as IBackgroundImage;
		const object = {
			...baseOptions,
			src,
			cropX,
			cropY,
			metadata,
			imageType,
			logoConfigType,
			isLogo,
			logoType,
			horizontalAlignment,
			verticalAlignment,
			brandId,
		};

		return object;
	}

	public staticAudio(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticAudio {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { src, metadata } = item as IStaticAudio;
		const object: IStaticAudio = {
			...baseOptions,
			src,
			metadata,
			speedFactor: 1,
		};
		return object;
	}

	public staticVideo(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticVideo {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { src } = item as IStaticVideo;
		const object = {
			...baseOptions,
			src: src,
			metadata: {},
			speedFactor: 1,
		};
		return object;
	}

	public staticVector(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticVector {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { src, colorMap, metadata, shapeType, colorNumber, shadowColorNumber, strokeOnly, brandId } =
			item as IStaticVector;
		return {
			...baseOptions,
			src,
			colorMap,
			metadata,
			shapeType,
			colorNumber,
			shadowColorNumber,
			strokeOnly,
			brandId,
		} as IStaticVector;
	}

	public staticPath(item: ILayer, options: Required<ILayer>, inGroup: boolean): IStaticPath {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { path, fill, metadata } = item as IStaticPath;
		const object = {
			...baseOptions,
			path,
			fill,
			metadata,
		};

		return object;
	}

	public background(item: ILayer, options: Required<ILayer>, inGroup: boolean): IBackground {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { fill, metadata, colorNumber, brandId } = item as IBackground;
		const object = {
			...baseOptions,
			fill,
			metadata,
			colorNumber,
			brandId,
		};

		return object;
	}

	public group(item: ILayer, options: Required<ILayer>, inGroup: boolean): IGroup {
		const baseOptions = this.getBaseOptions(item, options, inGroup);
		const { objects, metadata } = item as IGroup;
		const groupObjects = objects.map((object) => {
			return this.export(object, options, true);
		});
		return {
			...baseOptions,
			type: "Group",
			objects: groupObjects,
			metadata,
		};
	}

	public getBaseOptions(item: ILayer, options: Required<ILayer>, inGroup: boolean = false) {
		const {
			id,
			name,
			top,
			left,
			width,
			height,
			scaleX,
			scaleY,
			originX,
			originY,
			type,
			stroke,
			strokeWidth,
			strokeDashArray,
			opacity,
			angle,
			flipX,
			flipY,
			skewX,
			skewY,
			shadow,
			preview,
			isMainScene,
		} = item as Required<ILayer>;
		const baseOptions = {
			id,
			name: name ? name : type,
			angle,
			stroke,
			strokeWidth,
			strokeDashArray,
			left: inGroup ? left : left - options.left,
			top: inGroup ? top : top - options.top,
			width,
			height,
			opacity,
			originX,
			originY,
			scaleX,
			scaleY,
			type,
			flipX,
			flipY,
			skewX,
			skewY,
			visible: true,
			shadow,
			preview,
			isMainScene,
		};
		return baseOptions;
	}
}

export default ObjectExporter;
